import type { ReactNode } from 'react';
import type { ColumnDef, ColumnGroup } from '@shared-ui/components';
import { COLUMNS, SOURCE_GROUPS, type ColumnKey, type SourceGroup } from './columnRegistry';
import { InfoDot } from './InfoDot';

/**
 * A DataTable column that still says where it came from.
 *
 * The tint rides on `className`, which DataTable applies to the header and
 * every body cell; the source group is named once in the spanning band above
 * (see `sourceGroupRow`) rather than repeated in every header.
 */
export function sourceColumn<T>(
    col: ColumnKey,
    render: (row: T) => ReactNode,
    opts: Omit<ColumnDef<T>, 'key' | 'header' | 'render' | 'className'> & {
        /** Extra class on the header and every cell — e.g. `aq-num` to size a
         *  column to its digits rather than to its label. */
        extraClass?: string;
    } = {},
): ColumnDef<T> {
    const def = COLUMNS[col];
    const { extraClass, ...rest } = opts;
    return {
        key: col,
        className: `aq-src-${def.group}${extraClass ? ` ${extraClass}` : ''}`,
        mobileLabel: def.label,
        header: (
            <span className="aq-th">
                {def.label}
                <InfoDot title={def.label} detail={def.detail} group={def.group} />
            </span>
        ),
        render,
        ...rest,
    };
}

/**
 * The spanning band for a DataTable, built from the same registry the columns
 * are. Pass runs of `[group, span]` in column order; the spans must sum to the
 * column count, which is the one thing a band can get wrong.
 */
export function sourceGroupRow(runs: [SourceGroup, number][]): ColumnGroup[] {
    return runs.map(([group, span], i) => ({
        key: `${group}-${i}`,
        span,
        className: `aq-src-${group} aq-grouphead`,
        label: (
            <span className="aq-th">
                {SOURCE_GROUPS[group].label}
                {SOURCE_GROUPS[group].label
                    && <InfoDot title={SOURCE_GROUPS[group].label} detail={SOURCE_GROUPS[group].detail} group={group} />}
            </span>
        ),
    }));
}
